import { Injectable, inject } from '@angular/core';

import { TrackingService } from '@frontend/vanilla/core';

import { EventData } from '../model/gtm-event-model';
import { DataService } from './data.service';

@Injectable({
    providedIn: 'root',
})
export class GtmService {
    private dataService = inject(DataService);
    private trackingService = inject(TrackingService);
    gtmEventData: EventData;
    qdName: string;

    private defaulatPaymentData() {
        const params = new URLSearchParams(window?.location?.search);
        const launchSource = params?.get('launchSource')?.toLowerCase() || 'main';
        const sessionInfo = this.dataService.sessionInfo;
        return {
            source: `${launchSource} qd`,
            product: sessionInfo?.productId?.toLowerCase(),
        };
    }
    loadGTMData() {
        this.dataService.data = null;
        this.gtmEventData = {
            component: {},
            payment: { ...this.defaulatPaymentData() },
        };
    }

    getEventData() {
        return this.gtmEventData;
    }

    triggerEventWithData(eventName: any, data: any) {
        const event = eventName || data?.event;
        if (!data.payment) {
            data.payment = { ...this.defaulatPaymentData() };
        }
        this.trackingService.triggerEvent(event, data);
    }

    raiseCloseEvent() {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'high volume deposit declines - warning';
        eventData.component.ActionEvent = 'exit';
        eventData.component.PositionEvent = 'not applicable';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'cancel link';
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }

    raiseEvent(eventName?: any, pageName = '', paymentMethod = '', isOnload = false, contentPosition: string = '') {
        const eventData: any = structuredClone(this.gtmEventData);
        eventData.payment = eventData.payment ?? {};
        eventData.payment.method = paymentMethod?.toLowerCase();
        const response = this.dataService.data;
        if (isOnload) {
            eventData.eventName = 'quick_deposit_pop_up_load';
        }

        if (response) {
            if (eventName === 'deposit' && response?.status === 'SUCCESS') {
                eventData.payment.txnAmount = response?.amount ? response.amount.value : '';
                eventData.payment.txnCurrency = response.amount ? response.amount.currencyCode?.toLowerCase()?.toLowerCase() : '';
                eventData.payment.baseAmount = response.baseAmount ? response.baseAmount.value : '';
                eventData.payment.baseCurrency = response.baseAmount ? response.baseAmount.currencyCode?.toLowerCase() : '';
                eventData.payment.lifeTimeDeposits = response.totalDepositCount ? response.totalDepositCount : '';
                eventData.payment.depositTransactionId = response.transactionId ? response.transactionId : '';
            } else if ((eventName === 'paymentError' && response.errorResponse) || pageName === 'DepositDecline') {
                eventData.payment.error = response.errorResponse?.errorMessage?.toLowerCase() || 'deposit decline';
                eventData.payment.lifeTimeDeposits = response.totalDepositCount ? response.totalDepositCount : '';
            }
            if (eventName === 'paymentError' && response.kycCheckResponse && 'DEU' === response.kycCheckResponse.jurisdiction) {
                const { errorMessage } = response.kycCheckResponse.errorResponse || {};
                if (response.kycCheckResponse.productPreference === 'SPORTSBOOK' && errorMessage) {
                    if (errorMessage === 'KYC_GRACEPERIOD_COMPLETED') {
                        eventData.payment.error = 'kyc unverified – grace period ended';
                    } else if (errorMessage === 'KYC_LIMIT_EXCEEDED') {
                        eventData.payment.error = 'kyc unverified – 50k deposit limit amount exceeded';
                    }
                } else if (errorMessage) {
                    if (errorMessage === 'KYC_GRACEPERIOD_COMPLETED') {
                        eventData.payment.error = 'kyc unverified – grace period ended';
                    } else if (errorMessage === 'KYC_LIMIT_EXCEEDED') {
                        eventData.payment.error = ' kyc partial verified – 150 EUR deposit limit amount exceeded';
                    }
                }
                if (response.kycCheckResponse?.gbgVerified && response.kycCheckResponse.productPreference !== 'SPORTSBOOK') {
                    eventData.payment.error = 'kyc unverified – gaming verification required';
                }
            }
        }
        if (contentPosition) {
            eventData.component = {
                ContentPosition: contentPosition,
            };
        }
        if (eventData && eventData.component && Object.keys(eventData.component).length <= 0) {
            delete eventData.component;
        }
        this.triggerEventWithData(eventName, eventData);
    }

    raiseEventForCDRLimitExceeded(isCDRExceeded: any, paymentMethod: any, totalDepositCount: any) {
        const eventData = {
            payment: {
                error: isCDRExceeded ? 'central deposit limits exceeds budget' : 'central deposit limits exhausted budget',
                lifeTimeDeposits: totalDepositCount ?? '',
                method: paymentMethod?.toLowerCase(),
            },
        };
        this.triggerEventWithData('paymentError', eventData);
    }

    raiseEventForProrg30daysClick(positionEvent: any, url: any, linkName: any) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'click',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: linkName,
                URLClicked: url,
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForProrg30daysLoad(positionEvent: any) {
        const eventData = {
            event: 'contentView',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'load',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: positionEvent,
                URLClicked: 'not applicable',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForAffordabilityMsgBannerLoad(
        labelEvent: any,
        positionEvent: any,
        eventDetails: any,
        level: any,
        ageGroup: string,
        limitType: string,
        affGroup: string,
    ) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Affordability';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelEvent;
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = eventDetails;
        eventData.component.URLClicked = 'not applicable';
        eventData.component.AffordabilityJourney = level;
        eventData.component.AffAgeGroups = ageGroup?.toLowerCase();
        eventData.component.AffLimitType = limitType;
        eventData.component.AffordabilityGroups = affGroup?.toLowerCase();
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !ageGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData(eventName, eventData);
    }

    raiseEventForArcUnusualDeposit(eventType: any, locationEvent: any) {
        const eventData = structuredClone(this.gtmEventData);
        let eventName;
        if (eventType === 'unusualArcDepositInteractionMessage') {
            eventName = 'contentView';
            eventData.component.CategoryEvent = 'cashier';
            eventData.component.LabelEvent = 'high volume deposit declines warning';
            eventData.component.ActionEvent = 'load';
            eventData.component.PositionEvent = 'not applicable';
            eventData.component.LocationEvent = locationEvent;
            eventData.component.EventDetails = 'high volume deposit declines warning';
            eventData.component.URLClicked = 'not applicable';
        }
        this.triggerEventWithData(eventName, eventData);
    }
    // when user got any banner/toaster message loaded related to affordability
    raiseEventForAffordabilityBannerLoad(
        labelevent: string,
        eventDetails: string,
        positionEvent: string,
        ageGroup: string,
        limitType: string,
        affGroup: string,
        level: string,
    ) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelevent;
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = eventDetails;
        eventData.component.URLClicked = 'not applicable';
        eventData.component.AffAgeGroups = ageGroup?.toLowerCase();
        eventData.component.AffLimitType = limitType;
        eventData.component.AffordabilityGroups = affGroup?.toLowerCase();
        eventData.component.AffordabilityJourney = level;
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !ageGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData(eventName, eventData);
    }
    raiseEventForAffordabilityPaymentError(errorMessage: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'paymentError';
        eventData.payment = {};
        eventData.payment.error = errorMessage;
        eventData.payment.source = this.qdName;
        eventData.payment.depositMethod = 'standard deposit';
        this.triggerEventWithData(eventName, eventData);
    }
    // when user got any banner/toaster Button clicks related to affordability
    raiseEventForAffordabilityButtons(
        labelevent: string,
        buttonName: string,
        positionEvent: string,
        urlClicked: any,
        level: any,
        ageGroup: string,
        limitType: string,
        affGroup: string,
    ) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Affordability';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelevent;
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = buttonName;
        eventData.component.URLClicked = urlClicked;
        eventData.component.AffordabilityJourney = level;
        eventData.component.AffAgeGroups = ageGroup?.toLowerCase();
        eventData.component.AffLimitType = limitType;
        eventData.component.AffordabilityGroups = affGroup?.toLowerCase();
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !ageGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData(eventName, eventData);
    }
    // when user got any banner/toaster Link clicks related to affordability
    raiseEventForAffordabilityBannerlinks(labelevent: any, eventDetails: any, positionEvent: any, urlClicked: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelevent;
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = eventDetails + ' link';
        eventData.component.URLClicked = urlClicked;
        this.triggerEventWithData(eventName, eventData);
    }
    raiseEventForAffFRAMsgBannerLoadClick(
        labelEvent: any,
        actionEvent: any,
        positionEvent: any,
        eventDetails: any,
        url: any,
        level: any,
        ageGroup: string,
        limitType: string,
        affGroup: string,
    ) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Affordability';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelEvent;
        eventData.component.ActionEvent = actionEvent;
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = eventDetails;
        eventData.component.URLClicked = url;
        eventData.component.AffordabilityJourney = level;
        eventData.component.AffAgeGroups = ageGroup?.toLowerCase();
        eventData.component.AffLimitType = limitType;
        eventData.component.AffordabilityGroups = affGroup?.toLowerCase();
        eventData.component.flowName = 'fra';
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !ageGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData(eventName, eventData);
    }
    //When user reached almost 80% of limit
    raiseEventForAffordabilitySuccessMsg(labelevent: any, percent: any, limit: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelevent;
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = 'user reached almost ' + percent + '% of limit hardstop monthly ' + limit + 'k';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'hardstop monthly ' + limit + 'k';
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }
    raiseEventForAffordabilitySuccessMsgLinks(labelevent: any, percent: any, limit: any, eventDetails: any, url: any, level: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = labelevent;
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = 'user reached almost ' + percent + '% of limit hardstop monthly ' + limit + 'k';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = eventDetails;
        eventData.component.URLClicked = url;
        eventData.component.AffordabilityJourney = level;
        this.triggerEventWithData(eventName, eventData);
    }

    raiseEventForAffordabilityNetLossThresholdExceeded(level: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Affordability';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = 'aff ltm limit';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = 'user approaching ltm limit banner';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'user approaching ltm limit banner';
        eventData.component.URLClicked = 'not applicable';
        eventData.component.AffordabilityJourney = level;
        this.triggerEventWithData(eventName, eventData);
    }

    raiseEventForAffordabilityNetLossDepositCapClick(level: any, url: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Affordability';
        eventData.component.CategoryEvent = 'affordability';
        eventData.component.LabelEvent = 'aff ltm limit';
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = 'user approaching ltm limit banner';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'Loss Limit & Deposit Cap';
        eventData.component.URLClicked = url;
        eventData.component.AffordabilityJourney = level;
        this.triggerEventWithData(eventName, eventData);
    }

    // When the user clicks on deposit CTA
    raiseEventForDepositCTA() {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.eventName = eventName;
        eventData.component.CategoryEvent = 'navigation';
        eventData.component.LabelEvent = 'deposit button';
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = 'not applicable';
        eventData.component.LocationEvent = 'navigation';
        eventData.component.EventDetails = 'deposit';
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }
    // When the user clicks on deposit CTA Cashier Popup Screen Load
    raiseEventForDepositCTACashierPopupScreenLoad() {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.eventName = eventName;
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'deposit process';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = 'not applicable';
        eventData.component.LocationEvent = 'navigation';
        eventData.component.EventDetails = 'quick deposit screen';
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }
    // When the user clicks on deposit CTA Cashier Popup Screen Load
    raiseEventForODbannerLoad(positionEvent: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.eventName = eventName;
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'deposit process';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = positionEvent;
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }
    // When the user clicks on deposit CTA on Quick deposit Screen and sucessfully deposited
    raiseEventForDepositSuccessload() {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.component.CategoryEvent = 'deposit success';
        eventData.component.LabelEvent = 'success';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = 'not applicable';
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'deposit success';
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }

    raiseEventForUnusualDeposit(eventType: any, locationEvent: any, txnAmount: any) {
        const eventData = structuredClone(this.gtmEventData);
        let eventName;
        if (eventType === 'unusualDepositInteractionMessage') {
            eventName = 'contentView';
            eventData.component.CategoryEvent = 'cashier';
            eventData.component.LabelEvent = 'unusual deposit';
            eventData.component.ActionEvent = 'load';
            eventData.component.PositionEvent = 'not applicable';
            eventData.component.LocationEvent = locationEvent;
            eventData.component.EventDetails = 'unusual deposit notice';
            eventData.component.URLClicked = 'not applicable';
            eventData.component.Value = txnAmount;
        } else if (eventType === 'unusualDepositInterventionMessage') {
            eventName = 'contentView';
            eventData.component.CategoryEvent = 'cashier';
            eventData.component.LabelEvent = 'unusual deposit';
            eventData.component.ActionEvent = 'load';
            eventData.component.PositionEvent = 'not applicable';
            eventData.component.LocationEvent = locationEvent;
            eventData.component.EventDetails = 'temporary 24h deposit limits applied info message post success deposit';
            eventData.component.URLClicked = 'not applicable';
        } else if (eventType === 'continue') {
            eventName = 'Event.Tracking';
            eventData.component.CategoryEvent = 'cashier';
            eventData.component.LabelEvent = 'unusual deposit';
            eventData.component.ActionEvent = 'click';
            eventData.component.PositionEvent = 'continue';
            eventData.component.LocationEvent = locationEvent;
            eventData.component.EventDetails = 'unusual deposit notice';
            eventData.component.URLClicked = 'not applicable';
            eventData.component.Value = txnAmount;
        } else if (eventType === 'cancel') {
            eventName = 'Event.Tracking';
            eventData.component.CategoryEvent = 'cashier';
            eventData.component.LabelEvent = 'unusual deposit';
            eventData.component.ActionEvent = 'click';
            eventData.component.PositionEvent = 'cancel';
            eventData.component.LocationEvent = locationEvent;
            eventData.component.EventDetails = 'unusual deposit notice';
            eventData.component.URLClicked = 'not applicable';
            eventData.component.Value = txnAmount;
        } else if (eventType === 'interventionLiveChat') {
            eventName = 'Event.Tracking';
            eventData.component.CategoryEvent = 'cashier';
            eventData.component.LabelEvent = 'unusual deposit';
            eventData.component.ActionEvent = 'click';
            eventData.component.PositionEvent = 'live chat';
            eventData.component.LocationEvent = locationEvent;
            eventData.component.EventDetails = 'temporary 24h deposit limits applied info message post success deposit';
            eventData.component.URLClicked = 'not applicable';
            eventData.component.Value = txnAmount;
        }
        this.triggerEventWithData(eventName, eventData);
    }
    //occupational details banner clicks
    raiseEventForODbannerClicks(action: any, positionEvent: any, eventDet: any, url: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.eventName = eventName;
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'deposit process';
        eventData.component.ActionEvent = action;
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = eventDet;
        eventData.component.URLClicked = url;
        this.triggerEventWithData(eventName, eventData);
    }
    raiseEventWithSource(eventName: any, pageName: any, paymentMethod: any, source: any) {
        const eventData = structuredClone(this.gtmEventData);
        eventData.payment.method = paymentMethod?.toLowerCase();
        eventData.payment.source = source?.toLowerCase();
        const response = this.dataService.data;
        if (response) {
            if (eventName === 'deposit' && response.status === 'SUCCESS') {
                eventData.payment.txnAmount = response.amount ? response.amount.value : '';
                eventData.payment.txnCurrency = response.amount ? response.amount.currencyCode?.toLowerCase() : '';
                eventData.payment.baseAmount = response.baseAmount ? response.baseAmount.value : '';
                eventData.payment.baseCurrency = response.baseAmount ? response.baseAmount.currencyCode?.toLowerCase() : '';
                eventData.payment.lifeTimeDeposits = response.totalDepositCount ? response.totalDepositCount : '';
            } else if (eventName === 'paymentError' && (response.errorResponse || pageName === 'DepositDecline')) {
                eventData.payment.error = response.errorResponse?.errorMessage?.toLowerCase() || 'deposit decline';
                eventData.payment.lifeTimeDeposits = response.totalDepositCount ? response.totalDepositCount : '';
            }

            if (eventName === 'paymentError' && response.kycCheckResponse && 'DEU' === response.kycCheckResponse.jurisdiction) {
                if (response.kycCheckResponse.productPreference === 'SPORTSBOOK') {
                    if (response.kycCheckResponse.errorResponse) {
                        if (response.kycCheckResponse.errorResponse.errorMessage === 'KYC_GRACEPERIOD_COMPLETED') {
                            eventData.payment.error = 'kyc unverified – grace period ended';
                        } else if (response.kycCheckResponse.errorResponse.errorMessage === 'KYC_LIMIT_EXCEEDED') {
                            eventData.payment.error = 'kyc unverified – 50k deposit limit amount exceeded';
                        }
                    }
                } else {
                    if (response.kycCheckResponse.errorResponse) {
                        if (response.kycCheckResponse.errorResponse.errorMessage === 'KYC_GRACEPERIOD_COMPLETED') {
                            eventData.payment.error = 'kyc unverified – grace period ended';
                        } else if (response.kycCheckResponse.errorResponse.errorMessage === 'KYC_LIMIT_EXCEEDED') {
                            eventData.payment.error = ' kyc partial verified – 150 EUR deposit limit amount exceeded';
                        }
                    }
                }
                if (response.kycCheckResponse?.gbgVerified && response.kycCheckResponse.productPreference !== 'SPORTSBOOK') {
                    eventData.payment.error = 'kyc unverified – gaming verification required';
                }
            }
        }

        this.triggerEventWithData(eventName, eventData);
    }

    raiseComponentEvent(eventName: any, data: any) {
        this.triggerEventWithData(eventName, data);
    }
    intensivePlayerMsgLoad(paymentMethod: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'forbidden deposit';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = paymentMethod?.toLowerCase();
        eventData.component.LocationEvent = 'quick deposit page';
        eventData.component.EventDetails = 'credit card disabled message';
        eventData.component.URLClicked = 'not applicable';
        this.triggerEventWithData(eventName, eventData);
    }
    intensivePlayerMsgClickOnMoreInfo(clickUrl: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'forbidden deposit';
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = 'credit card disabled message ';
        eventData.component.LocationEvent = 'quick deposit page';
        (eventData.component.EventDetails = 'more info'), (eventData.component.URLClicked = clickUrl);
        this.triggerEventWithData(eventName, eventData);
    }
    riskyPlayerAccountTempBlock() {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'forbidden deposit';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = 'not applicable';
        eventData.component.LocationEvent = 'quick deposit page';
        (eventData.component.EventDetails = 'account restricted message'), (eventData.component.URLClicked = 'not applicable');
        this.triggerEventWithData(eventName, eventData);
    }
    riskyPlayerUnsuccessfulDep() {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'contentView';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'forbidden deposit';
        eventData.component.ActionEvent = 'load';
        eventData.component.PositionEvent = 'not applicable';
        eventData.component.LocationEvent = 'quick deposit page';
        (eventData.component.EventDetails = 'deposit unsuccessful message'), (eventData.component.URLClicked = 'not applicable');
        this.triggerEventWithData(eventName, eventData);
    }
    riskyPlayerClickOnLiveChat(positionEvent: any, clickUrl: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'Event.Tracking';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'forbidden deposit';
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = positionEvent;
        eventData.component.LocationEvent = 'quick deposit page';
        (eventData.component.EventDetails = 'live chat'), (eventData.component.URLClicked = clickUrl);
        this.triggerEventWithData(eventName, eventData);
    }
    // when user got any banner/toaster message loaded related to svoc max loss limits
    raiseEventForMaxLossLimitBannerLoad(isExceeded: any, ageGroup: any, limitType: any, affGroup: any, level: any) {
        const eventData = {
            event: 'contentView',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'svoc max loss limit',
                ActionEvent: 'load',
                PositionEvent: isExceeded ? 'user exhausted loss limit banner' : 'deposit crossing threshold banner',
                LocationEvent: 'quick deposit page',
                EventDetails: isExceeded ? 'user exhausted loss limit banner' : 'deposit crossing threshold banner',
                URLClicked: 'not applicable',
                AffAgeGroups: ageGroup?.toLowerCase(),
                AffLimitType: limitType,
                AffordabilityGroups: affGroup?.toLowerCase(),
                AffordabilityJourney: level,
            },
        };
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !ageGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData(null, eventData);
    }

    raiseEventForMaxLossLimitClick(
        isExceeded: boolean,
        linkName: any,
        url: any,
        ageGroup: string,
        limitType: string,
        affGroup: string,
        level: string,
    ) {
        const eventData: any = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'svoc max loss limit',
                ActionEvent: 'click',
                PositionEvent: isExceeded ? 'user exhausted loss limit banner' : 'deposit crossing threshold banner',
                LocationEvent: 'quick deposit page',
                EventDetails: linkName,
                URLClicked: url,
                AffAgeGroups: ageGroup?.toLowerCase(),
                AffLimitType: limitType,
                AffordabilityGroups: affGroup?.toLowerCase(),
                AffordabilityJourney: level,
            },
        };
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !ageGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData('', eventData);
    }
    copyQrCodeOrCtaOnPix(btnName: any, depositTransactionId: string) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'click',
                PositionEvent: 'pix',
                LocationEvent: 'quick deposit page',
                EventDetails: btnName,
                URLClicked: 'not applicable',
            },
            payment: { ...this.defaulatPaymentData(), depositTransactionId },
        };
        this.triggerEventWithData(null, eventData);
    }
    raiseEventForBrazilBankAccountReg(
        eventDetails: any,
        urlClicked: any,
        actionEvent: string,
        positionEvent: string,
        depositTransactionId?: string,
        contentPosition?: string | number,
    ) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: actionEvent,
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: urlClicked,
                ...(contentPosition !== undefined && { contentPosition }),
            },
            payment: { ...this.defaulatPaymentData(), depositTransactionId },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForQuickDepositClose(eventDetails: any, urlClicked: any, actionEvent: string, positionEvent: string) {
        const depositTransactionId = this.dataService?.data?.transactionId;
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: actionEvent,
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: urlClicked,
            },
            payment: {
                ...this.defaulatPaymentData(),
                method: this.dataService?.depositInfo?.paymentMethod?.toLowerCase(),
                ...(depositTransactionId && { depositTransactionId }),
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForRekycLoad(positionEvent: any) {
        const eventData = {
            event: 'contentView',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'load',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: positionEvent,
                URLClicked: 'not applicable',
                flowName: 'kyc refresh',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForRekycClick(action: any, positionEvent: any, eventDetails: any, urlClicked: any) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: action,
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: urlClicked,
                flowName: 'kyc refresh',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForAnnRekycLoad(eventDetails: string) {
        const eventData = {
            event: 'contentView',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'load',
                PositionEvent: 'partially verified/web verification passed',
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: 'not applicable',
                flowName: 'annual rekyc',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForAnnRekycClick(eventDetails: string, urlClicked: string) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'click',
                PositionEvent: 'partially verified/web verification passed',
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: urlClicked,
                flowName: 'annual rekyc',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForInsVerLoad(positionEvent: string) {
        const eventData = {
            event: 'contentView',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'load',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: positionEvent,
                URLClicked: 'not applicable',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForInsVerClick(positionEvent: string, eventDetails: string, urlClicked: string) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'click',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: urlClicked,
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForEsExpIdKycLoad(eventDetails: string, positionEvent: string) {
        const eventData = {
            eventName: 'contentView',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'load',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: 'not applicable',
                flowName: 'expired id rekyc',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForEsExpIdKycClick(eventDetails: string, urlClicked: string, positionEvent: string) {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'click',
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: urlClicked,
                flowName: 'expired id rekyc',
            },
        };
        this.triggerEventWithData('', eventData);
    }
    // Deposit Error tracking
    // GTM Events:  Scenario: Users type less than 3 numbers and clicks outside the field (only fire error if he clicks outside field)
    // GTM Events:  Scenario: Users clicks has an enter amount error
    paymentErrorEvent(error: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'paymentError';
        eventData.payment = {};
        eventData.payment.error = error;
        eventData.payment.source = this.qdName;
        eventData.payment.depositMethod = 'quick deposit';
        this.triggerEventWithData(eventName, eventData);
    }
    //GTM event for tracking When user clicks on 'More Options' during Quick Deposit
    raiseEventForMoreOptions() {
        const eventData = {
            event: 'Event.Tracking',
            component: {
                CategoryEvent: 'cashier',
                LabelEvent: 'deposit process',
                ActionEvent: 'click',
                PositionEvent: 'quick deposit',
                LocationEvent: 'quick deposit page',
                EventDetails: 'more options',
                URLClicked: 'not applicable',
            },
            payment: { ...this.defaulatPaymentData() },
        };
        this.triggerEventWithData('', eventData);
    }
    raiseEventForSVOCDepositCapLinks(locationEvent: any, eventDetails: any, urlClicked: any, pay: any) {
        const eventData: any = { ...this.gtmEventData };
        const eventName = 'Event.Tracking';
        eventData.component.CategoryEvent = 'cashier';
        eventData.component.LabelEvent = 'deposit process';
        eventData.component.ActionEvent = 'click';
        eventData.component.PositionEvent = pay?.toLowerCase();
        eventData.component.LocationEvent = locationEvent;
        eventData.component.EventDetails = eventDetails;
        eventData.component.URLClicked = urlClicked;
        eventData.payment = eventData.payment ?? {};
        eventData.payment.method = pay?.toLowerCase();
        this.triggerEventWithData(eventName, eventData);
    }
    raiseEventForSVOCValidations(errorMessage: any, pay: any) {
        const eventData = structuredClone(this.gtmEventData);
        const eventName = 'paymentError';
        eventData.payment = {};
        eventData.payment.error = errorMessage;
        eventData.payment.source = 'quick deposit page';
        eventData.payment.method = pay?.toLowerCase();
        this.triggerEventWithData(eventName, eventData);
    }
    raiseEventForaffordabilityMsgsLoadandClick(
        labelEvent: any,
        actionEvent: any,
        positionEvent: any,
        eventDetails: any,
        url: any,
        level: any,
        affGroup: any,
        affAgeGroup: any,
        limitType: any,
    ) {
        const eventData = {
            event: 'Event.Affordability',
            component: {
                CategoryEvent: 'affordability',
                LabelEvent: labelEvent,
                ActionEvent: actionEvent,
                PositionEvent: positionEvent,
                LocationEvent: 'quick deposit page',
                EventDetails: eventDetails,
                URLClicked: url,
                AffordabilityJourney: level?.toLowerCase(),
                AffordabilityGroups: affGroup?.toLowerCase(),
                AffAgeGroups: affAgeGroup?.toLowerCase(),
                AffLimitType: limitType,
            },
        };
        !affGroup ? delete eventData.component.AffordabilityGroups : null;
        !level ? delete eventData.component.AffordabilityJourney : null;
        !affAgeGroup ? delete eventData.component.AffAgeGroups : null;
        !limitType ? delete eventData.component.AffLimitType : null;
        this.triggerEventWithData('', eventData);
    }
}
